import { areas, topics, skills } from 'shared/src/knowledge/knowledgeGraphNodes';
import type { Skill, Topic, Area } from 'shared/definitions/skill-definitions';
import utils from '../utils';

let tree: Area[];

interface SkillQuery {
  textId?: string;
  id: number;
}

export { default as processRhythm } from './processRhythm';
// eslint-disable-next-line import/no-cycle
export { default as QuestionGenerator } from './questionGenerator';
export { areas, topics, skills } from 'shared/src/knowledge/knowledgeGraphNodes';

export function equals(skillA: Skill | string | number, skillB: Skill | string | number): boolean {
  return makeInt(skillA) === makeInt(skillB);
}

export function makeString(skill: any): string {
  const skillObj = getSkill(skill);
  if (typeof skillObj !== 'undefined') return skillObj.textId;
  return undefined;
}

export function makeInt(skill: any): number | void {
  const skillObj = getSkill(skill);
  if (typeof skillObj !== 'undefined') return skillObj.id;
  return undefined;
}

export function getSkill(skill: any): Skill | void {
  let skillObj;
  // Note: there is one skill with a non-integer id. id: 11.5, textId: naturals
  if (utils.isNumeric(skill)) {
    skillObj = skills.find((obj) => obj.id === Number(skill));
  } else if (utils.isString(skill)) {
    skillObj = skills.find((obj) => obj.textId === skill);
  } else if (utils.isObject(skill) && ((skill as SkillQuery).textId || (skill as SkillQuery).id)) {
    const id = (skill as SkillQuery).textId || (skill as SkillQuery).id;
    return getSkill(id);
  }

  // like array.find, returns undefined if not found.
  return skillObj;
}

export function getTopic(topic: any, options = { includeSkills: false }): Topic | void {
  let topicObj;
  if (utils.isNumeric(topic) && utils.isInteger(Number(topic))) {
    topicObj = topics.find((obj) => obj.id === Number(topic));
  } else if (utils.isString(topic)) {
    topicObj = topics.find((obj) => obj.textId === topic);
  } else if (utils.isObject(topic) && ((topic as SkillQuery).textId || (topic as SkillQuery).id)) {
    const id = (topic as SkillQuery).textId || (topic as SkillQuery).id;
    return getTopic(id, options);
  }

  // If we need to includeSkills, add them:
  if (options.includeSkills) {
    (topicObj as Topic).skills = skills.filter((skill) => skill.topicId === topicObj.id);
  }
  return topicObj;
}

export function getArea(area: any): void | Area {
  let areaObj;
  if (utils.isNumeric(area) && utils.isInteger(Number(area))) {
    areaObj = areas.find((obj) => obj.id === Number(area));
  } else if (utils.isString(area)) {
    areaObj = areas.find((obj) => obj.textId === area);
  } else if (utils.isObject(area) && ((area as SkillQuery).textId || (area as SkillQuery).id)) {
    const id = (area as SkillQuery).textId || (area as SkillQuery).id;
    return getArea(id);
  }
  // like array.find, returns undefined if not found.
  return areaObj;
}

export function getTree(): Area[] {
  if (typeof tree !== 'undefined') return tree;

  // Group skills by topic id:
  // eslint-disable-next-line @typescript-eslint/no-shadow
  const topics = utils.groupBy(skills as Skill[], 'topicId').map((topicSkills: Skill[]) => {
    const topic: Topic = utils.clone(getTopic(topicSkills[0].topicId) as Topic);
    topic.skills = topicSkills;
    return topic;
  });
  // Group topics by area:
  // eslint-disable-next-line @typescript-eslint/no-shadow
  const areas = utils.groupBy(topics, 'areaId').map((areaTopics: Topic[]) => {
    const area: Area = utils.clone(getArea(areaTopics[0].areaId) as Area);
    area.topics = areaTopics;
    return area;
  });

  tree = areas;
  return areas;
}
